import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../api';
import { useAttention } from '../attention';
import { useRange, withRange } from '../range';
import { Band, Card } from '../components/Card';
import EmptyState from '../components/EmptyState';
import KpiTile from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { formatCompactNumber, formatPct, formatRelative } from '../format';

/**
 * Home — "is anything wrong, and how are we doing".
 *
 * The old Overview was six static numbers with no trend, no comparison and
 * nothing clickable. You landed on the panel and there was nowhere to go,
 * which is most of why the whole thing read as a pile of disconnected
 * screens. Three changes fix that:
 *
 *   1. **Needs attention comes first.** The product doc names "things that
 *      should wake someone up" as one of three reasons this panel exists, and
 *      nothing implemented it.
 *   2. **Every number carries a direction** — measured against the
 *      immediately preceding window of the same length, so "840 billable
 *      clicks" is legible without knowing the history.
 *   3. **Every tile is a door.** Clicking one lands on the section that
 *      explains it, with the current time range carried along so the number
 *      on the next page matches the one just clicked.
 */
const SEVERITY = {
  critical: { dot: 'bg-status-critical', text: 'text-status-critical', ring: 'border-status-critical/25 bg-status-critical/[0.04]' },
  warning: { dot: 'bg-status-warning', text: 'text-[#8a5a00]', ring: 'border-status-warning/30 bg-status-warning/[0.06]' },
  info: { dot: 'bg-accent', text: 'text-accent-600', ring: 'border-accent/25 bg-accent-50/50' },
};

function pctChange(now, before) {
  if (now == null || before == null) return null;
  if (before === 0) return now === 0 ? 0 : null; // growth from zero has no meaningful percentage
  return ((now - before) / before) * 100;
}

function HealthStrip({ status, checkedAt, count }) {
  const ok = status === 'good';
  const tone = ok ? SEVERITY.info : SEVERITY[status] || SEVERITY.info;
  return (
    <div className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 ${ok ? 'border-hairline bg-surface' : tone.ring}`}>
      <div className="flex items-center gap-2.5">
        <span className={`h-2 w-2 rounded-full ${ok ? 'bg-status-good' : tone.dot}`} aria-hidden="true" />
        <span className="text-sm font-medium text-ink">
          {ok ? 'Nothing needs attention' : `${count} thing${count === 1 ? '' : 's'} need attention`}
        </span>
        <span className="text-xs text-ink-muted">checked {formatRelative(checkedAt)}</span>
      </div>
      <Link to="/system" className="text-xs font-medium text-raspberry-600 hover:text-raspberry-700">
        Health &amp; cost →
      </Link>
    </div>
  );
}

function AttentionList({ items }) {
  return (
    <ul className="divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-surface shadow-sm">
      {items.map((item) => {
        const tone = SEVERITY[item.severity] || SEVERITY.info;
        return (
          <li key={item.key}>
            <Link to={item.to} className="flex items-start gap-3 px-4 py-3 hover:bg-surface-page">
              <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${tone.dot}`} aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-medium ${tone.text}`}>{item.title}</span>
                <span className="block text-xs leading-snug text-ink-secondary">{item.detail}</span>
              </span>
              <span className="mt-0.5 flex-shrink-0 text-xs font-medium text-raspberry-600">Open →</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export default function HomePage() {
  const range = useRange();
  const { items, health, loading: attentionLoading } = useAttention();

  const [data, setData] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [signups, setSignups] = useState(null);
  const [tryon, setTryon] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    // The same window again, immediately before this one — the only honest
    // baseline for a delta. Comparing a 24h window against a 30d average
    // would make every Monday morning look like a collapse.
    const since = new Date(range.since);
    const until = new Date(range.until);
    const span = until - since;
    const prevParams = {
      since: new Date(since.getTime() - span).toISOString(),
      until: since.toISOString(),
    };

    try {
      const [now, before, users, tryonResp] = await Promise.all([
        adminApi.get('/overview', { params: { since: range.since, until: range.until } }),
        adminApi.get('/overview', { params: prevParams }).catch(() => null),
        // Home only needs the signup trend from this payload — not the cohort
        // table, which is the largest thing in it.
        adminApi.get('/users/aggregate', { params: { cohort_page_size: 1 } }).catch(() => null),
        adminApi.get('/tryon/overview', { params: { days: range.days } }).catch(() => null),
      ]);
      setData(now.data);
      setPrevious(before?.data || null);
      setSignups(users?.data?.signup_trend || null);
      setTryon(tryonResp?.data || null);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load Home'));
    } finally {
      setLoading(false);
    }
  }, [range.since, range.until, range.days]);

  useEffect(() => { load(); }, [load]);

  const activity = data?.activity;
  const prevActivity = previous?.activity;
  const totals = data?.totals;

  // The sparkline shows the last 14 days of signups regardless of the active
  // range — a 24h window has no shape to draw, and the tile's own number
  // already respects the range.
  const signupSpark = signups ? signups.slice(-14).map((d) => d.signups) : null;
  const tryonSuccess = tryon?.totals?.success_rate_pct;

  return (
    <div>
      <PageHeader
        title="Home"
        hint={`How MAHI is doing — ${range.label.toLowerCase()}.`}
      />

      <HealthStrip
        status={health?.status || 'good'}
        checkedAt={health?.checked_at}
        count={items.length}
      />

      {items.length > 0 && (
        <Band title="Needs attention">
          <AttentionList items={items} />
        </Band>
      )}

      <Band title={`Activity — ${range.label.toLowerCase()}`}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="New signups"
            value={formatCompactNumber(activity?.new_signups)}
            delta={pctChange(activity?.new_signups, prevActivity?.new_signups)}
            goodWhen="up"
            trend={signupSpark}
            to={withRange('/growth', range)}
            loading={loading}
            caption="vs. the previous window of the same length"
          />
          <KpiTile
            label="Billable clicks"
            value={formatCompactNumber(activity?.billable_clicks)}
            delta={pctChange(activity?.billable_clicks, prevActivity?.billable_clicks)}
            goodWhen="up"
            to={withRange('/growth/funnel', range)}
            loading={loading}
            caption="Deduplicated, bot-filtered. Not a purchase count."
          />
          <KpiTile
            label="Try-ons completed"
            value={formatCompactNumber(activity?.tryons_completed)}
            delta={pctChange(activity?.tryons_completed, prevActivity?.tryons_completed)}
            goodWhen="up"
            to={withRange('/ai/tryon', range)}
            loading={loading}
            caption="Failed and in-progress attempts are not counted."
          />
          <KpiTile
            label="Try-on success"
            value={formatPct(tryonSuccess, 1)}
            goodWhen="up"
            to={withRange('/ai/tryon', range)}
            loading={loading}
            caption={tryon?.status === 'no_data' ? 'No pipeline data in this window.' : 'Completed as a share of finished attempts.'}
          />
        </div>
      </Band>

      <Band title="Platform">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiTile
            label="Users"
            value={formatCompactNumber(totals?.users)}
            to="/growth"
            loading={loading}
            caption="All time"
          />
          <KpiTile
            label="Brands"
            value={formatCompactNumber(totals?.brands)}
            to="/catalog"
            loading={loading}
            caption="Crawl sources — every brand site MAHI carries products from"
          />
          <KpiTile
            label="Products"
            value={formatCompactNumber(totals?.products)}
            to={withRange('/catalog/products', range)}
            loading={loading}
            caption="Active catalogue items"
          />
        </div>
      </Band>

      <Band title="User feedback">
        {/* An honest gap, per the product doc's own instruction that this tile
            "is explicitly absent, not faked with a proxy". Substituting an
            NPS-shaped number derived from saves would be worse than nothing:
            it would get quoted. */}
        <EmptyState
          tone="gap"
          title="Not collected yet"
          detail="There is no in-app rating, thumbs up/down, or store-review ingestion, so there is no feedback.submitted event to count. This tile stays empty rather than standing in a proxy metric that would get quoted as satisfaction."
        />
      </Band>

      {!attentionLoading && items.length === 0 && !loading && (
        <Card>
          <p className="text-sm text-ink-secondary">
            Nothing is flagged: no brand applications waiting, no failed or stale crawls, and the
            try-on and stylist pipelines are inside their expected ranges.
          </p>
        </Card>
      )}
    </div>
  );
}
